'use client';

import { useCallback, useMemo, useRef, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { omit } from 'radash';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  ConfirmationDialog,
  ListPageLayout,
  ListPageOptionsSwitch,
} from '@terra/ui-design-system';
import { Radar01Icon } from '@terra/ui-icons';
import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';
import type { GroupedSignal } from '@terra/shared-contracts-signals';
import {
  useAppTargetId,
  useTargetId,
  useTargetType,
} from '../../store/projectStore';
import { useMainContentTabs, usePanelCollapse } from '../../store/layoutStore';
import { AiSignalsAssetKindNav } from '../../hooks/useAiSignalsKindFilter';
import { useInvalidateApplicationDomainTree } from '../../hooks/api/useApplicationDomainTree';
import { CollapsedPanelStrip } from '../layout/CollapsedPanelStrip';
import { COLLAPSED_SIDE_PANEL_WIDTH_PX } from '../../hooks/layout/collapsedPanelWidth';
import { SignalsContextNavigation } from './signals-header/SignalsContextNavigation';
import { SignalsFilterPanel } from './signals-header/SignalsFilterPanel';
import { SignalsBulkActionsBar } from './signals-header/SignalsBulkActionsBar';
import { SignalsTable } from './signals-table/SignalsTable';
import {
  SignalsTableContextProvider,
  type SignalsTableContextValue,
} from './signals-table/SignalsTableContext';
import {
  useSignalsScope,
  type SignalsFilterScope,
} from './signals-table/useSignalsScope';
import { useSignalsLiveUpdates } from './signals-table/useSignalsLiveUpdates';
import { useSignalsListFilters } from './signals-table/useSignalsListFilters';
import { useSignalsListData } from './signals-table/useSignalsListData';
import { useSignalsSelection } from './signals-table/useSignalsSelection';
import { useSignalsBulkSend } from './signals-table/useSignalsBulkSend';
import { useSignalsBulkDelete } from './signals-table/useSignalsBulkDelete';
import { useSignalsNavigation } from './signals-table/useSignalsNavigation';
import { useSignalsQueryInvalidation } from './signals-table/useSignalsQueryInvalidation';
import { useTabScopedSignalsFilters } from '../../store/tab/signalsTabScopedFilterStore';
import { useSignalsLifecycleViewCounts } from '../../hooks/useSignalsLifecycleViewCounts';
import { useAiSignalsKindCounts } from '../../hooks/useAiSignalsKindCounts';
import {
  buildAiKindSignalCountsFilters,
  buildSignalsSelectionScopeKey,
} from './signals-table/signals-list-filters.model';
import { useSignalsTableRows } from './signals-table/useSignalsTableRows';
import { SignalsViewOptionsMenu } from './signals-header/SignalsViewOptionsMenu';

type SignalsContentProps = {
  header?: ReactNode;
  endpointId?: string;
  endpointUrl?: string;
  surfaceAssetEntityId?: string;
  surfaceAssetKind?: string;
  filterScope?: SignalsFilterScope;
  enableRejectedSignalsToggle?: boolean;
  lifecycleStateOverride?: LifecycleStateFilterDto;
};

const toggleSetValue = (values: ReadonlySet<string>, value: string) => {
  const nextValues = new Set(values);

  if (nextValues.has(value)) {
    nextValues.delete(value);
  } else {
    nextValues.add(value);
  }

  return nextValues;
};

export function SignalsContent({
  header,
  endpointId,
  surfaceAssetEntityId,
  surfaceAssetKind,
  filterScope = 'application',
  enableRejectedSignalsToggle = false,
  lifecycleStateOverride,
}: SignalsContentProps) {
  const t = useTranslations('proxy.signals');
  const tSignals = useTranslations('signals');
  const tCommon = useTranslations('common');
  const tLifecycle = useTranslations('designSystem.lifecycle');
  const tLayout = useTranslations('layout');
  const tDesignSystem = useTranslations('designSystem.applicationAssets');
  const targetId = useTargetId();
  const targetType = useTargetType();
  const appTargetId = useAppTargetId();
  const { activePageScrollKey } = useMainContentTabs();
  const { isAiSignalsKindNavCollapsed, toggleAiSignalsKindNavCollapsed } =
    usePanelCollapse();
  const invalidateApplicationDomainTree = useInvalidateApplicationDomainTree();
  const { invalidateSignalCounts } = useSignalsQueryInvalidation();
  const scopeModel = useSignalsScope({ endpointId, filterScope });

  useSignalsLiveUpdates();

  const listFilters = useSignalsListFilters({
    scope: scopeModel.scope,
    enableRejectedSignalsToggle,
    lifecycleStateOverride,
  });
  const { isRejectedSignalsView, countsFilters } = listFilters;
  const showLifecycleViews = listFilters.lifecycle.showLifecycleSelector;
  const lifecycleCountsFilters = useMemo(() => {
    if (!countsFilters) {
      return undefined;
    }

    if (!showLifecycleViews) {
      return countsFilters;
    }

    return omit(countsFilters, ['stage']);
  }, [countsFilters, showLifecycleViews]);
  const aiKindCountsFilters = useMemo(
    () => buildAiKindSignalCountsFilters(countsFilters),
    [countsFilters],
  );
  const aiKindCountsByKind = useAiSignalsKindCounts({
    targetId,
    isAiApplication: scopeModel.aiKindNav.isAiApplication,
    endpointId,
    profile: scopeModel.aiKindNav.profile,
    filters: aiKindCountsFilters,
  });
  const listData = useSignalsListData({
    source: { endpointId, surfaceAssetEntityId, surfaceAssetKind },
    listFilters,
    scopeModel,
  });
  const { lifecycleCounts } = useSignalsLifecycleViewCounts({
    targetId,
    targetType,
    endpointId,
    surfaceAssetEntityId,
    surfaceAssetKind,
    includeApplicationLevelGroup: scopeModel.scope.includeApplicationLevelGroup,
    filters: lifecycleCountsFilters,
    enabled: showLifecycleViews && !isRejectedSignalsView,
  });
  const selection = useSignalsSelection({
    signals: listData.signals,
    selectableTotalCount: listData.selectableTotalCount,
    fetchAllSignals: listData.fetchAllSignals,
  });
  const bulkSend = useSignalsBulkSend({
    selection,
    isNetworkTarget: listFilters.isNetworkTarget,
  });
  const selectionScopeKey = buildSignalsSelectionScopeKey({
    targetId,
    targetType,
    endpointId,
    filters: listFilters.filters,
    scope: scopeModel.scope,
    isRejectedView: isRejectedSignalsView,
  });
  const bulkDelete = useSignalsBulkDelete({
    selection,
    selectionScopeKey,
    removeSignals: listData.removeSignals,
    refetch: listData.refetch,
  });
  const navigation = useSignalsNavigation({
    isRejectedView: isRejectedSignalsView,
  });
  const {
    expandedSignalIds: persistedExpandedSignalIds,
    setExpandedSignalIds,
  } = useTabScopedSignalsFilters();
  const expandedSignalIds = useMemo(
    () => new Set(persistedExpandedSignalIds),
    [persistedExpandedSignalIds],
  );
  const expandedSignalIdsRef = useRef(expandedSignalIds);

  expandedSignalIdsRef.current = expandedSignalIds;
  const tableRows = useSignalsTableRows({
    signals: listData.signals,
    expandedSignalIds,
    grouping: listFilters.grouping,
    groups: listData.groups,
    hasMore: listData.hasNextPage,
    resetKey: selectionScopeKey,
  });
  const isSelectionLocked = bulkSend.isRunning || bulkDelete.isBusy;
  const { selectAll, selectSignal } = selection;
  const handleSelectAll = useCallback(
    (shouldSelect: boolean) => {
      if (!isSelectionLocked) {
        void selectAll(shouldSelect);
      }
    },
    [isSelectionLocked, selectAll],
  );
  const handleSelectSignal = useCallback(
    (params: { signalId: string; shouldSelect: boolean }) => {
      if (!isSelectionLocked) {
        selectSignal(params);
      }
    },
    [isSelectionLocked, selectSignal],
  );
  const handleToggleExpanded = useCallback(
    (signalId: string) => {
      const nextExpandedSignalIds = toggleSetValue(
        expandedSignalIdsRef.current,
        signalId,
      );

      expandedSignalIdsRef.current = nextExpandedSignalIds;
      setExpandedSignalIds(Array.from(nextExpandedSignalIds));
    },
    [setExpandedSignalIds],
  );
  const tableContextValue = useMemo(
    (): SignalsTableContextValue => ({
      isRejectedView: isRejectedSignalsView,
      isSelectionDisabled: isSelectionLocked,
      headerCheckboxState: selection.areAllSelected
        ? true
        : selection.hasPartialSelection
          ? 'indeterminate'
          : false,
      isSelectAllDisabled:
        isSelectionLocked || listData.selectableTotalCount === 0,
      expandedSignalIds,
      boothAttackSignalId: listData.boothAttackSignalId,
      navigation,
      isSignalSelected: selection.isSignalSelected,
      onSelectAll: handleSelectAll,
      onSelectSignal: handleSelectSignal,
      onToggleExpanded: handleToggleExpanded,
      onUpdateSignal: listData.updateSignal,
    }),
    [
      expandedSignalIds,
      handleSelectAll,
      handleSelectSignal,
      handleToggleExpanded,
      isRejectedSignalsView,
      isSelectionLocked,
      listData.boothAttackSignalId,
      listData.selectableTotalCount,
      listData.updateSignal,
      navigation,
      selection.areAllSelected,
      selection.hasPartialSelection,
      selection.isSignalSelected,
    ],
  );
  const handleRefresh = () => {
    void listData.refetch();
    invalidateSignalCounts();

    if (appTargetId) {
      void invalidateApplicationDomainTree({ applicationId: appTargetId });
    }
  };

  const handleOpenSignal = useCallback(
    (signal: GroupedSignal) =>
      navigation.openTab(navigation.getSignalTab(signal)),
    [navigation],
  );
  const handleOpenSignalContextMenu = useCallback(
    ({ event, signal }: { event: MouseEvent; signal: GroupedSignal }) =>
      navigation.openTabContextMenu({
        event,
        tab: navigation.getSignalTab(signal),
      }),
    [navigation],
  );
  const scrollId =
    activePageScrollKey !== undefined
      ? `${activePageScrollKey}:signals-list`
      : endpointId
        ? `signals-endpoint-${targetId ?? 'unknown'}-${endpointId}`
        : surfaceAssetEntityId
          ? `signals-surface-asset-${targetId ?? 'unknown'}-${surfaceAssetKind ?? 'unknown'}-${surfaceAssetEntityId}`
          : `signals-list-${targetId ?? 'unknown'}`;
  const { aiKindNav } = scopeModel;
  const mutationsDisabledTooltip = listFilters.lifecycle
    .areArchivedMutationsDisabled
    ? tLifecycle('archivedSignalActionTooltip')
    : null;
  const signalsList = (
    <Box data-tour-id="signals-panel" flex="1" minW="0" h="full">
      <ListPageLayout
        header={header}
        navigation={
          <SignalsContextNavigation
            isStageChipsEnabled={!isRejectedSignalsView && !showLifecycleViews}
            showLifecycleViews={showLifecycleViews && !isRejectedSignalsView}
            lifecycleState={listFilters.lifecycleState}
            onLifecycleStateChange={listFilters.setLifecycleState}
            lifecycleCounts={lifecycleCounts}
            stageFilter={listFilters.stageFilter}
            onStageFilterChange={listFilters.setStageFilter}
            stageCounts={listData.stageCounts}
            searchTerm={listFilters.searchTerm}
            onSearchTermChange={listFilters.setSearchTerm}
            onRefresh={handleRefresh}
            isLoading={listData.isLoading}
            optionsMenu={
              <SignalsViewOptionsMenu
                canGroup={listFilters.canGroup}
                showSourceOption={listFilters.showThirdPartySource}
                showAssetOption={listFilters.showAssetGrouping}
                groupByField={listFilters.grouping?.field ?? null}
                onGroupByFieldChange={listFilters.setGroupByField}
                areAllGroupsCollapsed={tableRows.areAllGroupsCollapsed}
                onExpandAllGroups={tableRows.expandAllGroups}
                onCollapseAllGroups={tableRows.collapseAllGroups}
              >
                {enableRejectedSignalsToggle ? (
                  <ListPageOptionsSwitch
                    label={t('options.viewRejected')}
                    checked={listFilters.viewRejectedSignals}
                    onCheckedChange={listFilters.setViewRejectedSignals}
                  />
                ) : null}
              </SignalsViewOptionsMenu>
            }
          />
        }
        filters={
          isRejectedSignalsView ? null : (
            <SignalsFilterPanel
              filters={listFilters}
              hideLifecycleFilter={showLifecycleViews}
              showFilteredCounter={listData.hasActiveFilters}
              filteredCount={listData.filteredCount}
              totalCount={listData.headerTotalCount}
              selection={
                selection.selectedCount > 0 ? (
                  <SignalsBulkActionsBar
                    selectedCount={selection.selectedCount}
                    outOfTestingScopeCount={bulkSend.outOfTestingScopeCount}
                    isBusy={isSelectionLocked}
                    mutationsDisabledTooltip={mutationsDisabledTooltip}
                    onClearSelection={selection.clear}
                    onSendToNeo={() => void bulkSend.sendToNeo()}
                    onSendToCopilot={() => void bulkSend.sendToCopilot()}
                    onDelete={() => void bulkDelete.prepare()}
                  />
                ) : null
              }
            />
          )
        }
      >
        <SignalsTable
          key={isRejectedSignalsView ? 'rejected' : 'active'}
          rows={tableRows.rows}
          signalCount={listData.signals.length}
          loadMoreRowIndex={tableRows.loadMoreRowIndex}
          scrollId={scrollId}
          sortState={listFilters.sortState}
          canSort={listFilters.canSort}
          isRejectedView={isRejectedSignalsView}
          showSourceColumn={listFilters.showThirdPartySource}
          showAppWideColumn={listFilters.showAppWide}
          isLoading={listData.isLoading}
          isFetchingMore={listData.isLoadingMore}
          hasMore={listData.hasNextPage}
          hasActiveFilters={listData.hasActiveFilters}
          onLoadMore={listData.loadMore}
          onSortStateChange={listFilters.setSortState}
          onToggleGroup={tableRows.toggleGroup}
          onOpenSignal={handleOpenSignal}
          onOpenSignalContextMenu={handleOpenSignalContextMenu}
        />

        <ConfirmationDialog
          isOpen={bulkDelete.isDialogOpen}
          onOpenChange={({ open }) => {
            if (!open) {
              bulkDelete.cancel();
            }
          }}
          onConfirm={() => void bulkDelete.confirm()}
          loading={bulkDelete.isDeleting}
          variant="destructive"
          title={tSignals('bulkActions.deleteDialogTitle')}
          description={tSignals('bulkActions.deleteDialogDescription', {
            count: bulkDelete.pendingCount,
          })}
          confirmText={tCommon('delete')}
          cancelText={tCommon('cancel')}
        />
      </ListPageLayout>
    </Box>
  );

  return (
    <SignalsTableContextProvider value={tableContextValue}>
      {aiKindNav.isAiApplication ? (
        <HStack
          gap={0}
          w="full"
          h="full"
          alignItems="stretch"
          overflow="hidden"
        >
          {isAiSignalsKindNavCollapsed ? (
            <Box
              w={`${COLLAPSED_SIDE_PANEL_WIDTH_PX}px`}
              minW={`${COLLAPSED_SIDE_PANEL_WIDTH_PX}px`}
              h="full"
              flexShrink={0}
            >
              <CollapsedPanelStrip
                position="left"
                title={tDesignSystem('allAssets')}
                icon={Radar01Icon}
                onClick={toggleAiSignalsKindNavCollapsed}
              />
            </Box>
          ) : (
            <AiSignalsAssetKindNav
              selectedKind={aiKindNav.selectedKind}
              countsByKind={aiKindCountsByKind}
              profile={aiKindNav.profile}
              onSelectKind={aiKindNav.setSelectedKind}
              onMinimize={toggleAiSignalsKindNavCollapsed}
              minimizeLabel={tLayout('minimizePanel')}
            />
          )}
          {signalsList}
        </HStack>
      ) : (
        signalsList
      )}
    </SignalsTableContextProvider>
  );
}
